'use client';

import { useTranslations } from 'next-intl';
import { Loader2, ScanText } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { useHashParamDialog } from '@/hooks/use-hash-dialog';
import { PermissionGate } from '@/features/auth';
import { useStartOcrMutation } from '../services/ocr.mutations';
import { useOcrToast } from '../utils/ocr-toast';
import { OCR_HASH } from '../constants';
import { OcrTemplatePoolDialog } from './ocr-template-pool-dialog';

interface StartOcrButtonProps {
  containerId: number;
  /**
   * Optional — when provided, opens the multi-template pool dialog so the
   * operator can pick which OCR templates to run. Without it, the button
   * falls back to the legacy single-template flow (uses whatever template
   * the registration's dynamic form has).
   */
  registrationId?: number;
  disabled?: boolean;
  /** Display name shown in the rich toast — falls back to a generic message. */
  containerLabel?: string;
}

export function StartOcrButton({
  containerId,
  registrationId,
  disabled,
  containerLabel
}: StartOcrButtonProps) {
  const t = useTranslations('ocr');
  const startMutation = useStartOcrMutation();
  const toasts = useOcrToast();
  // Template-pool dialog open-state lives in the URL hash
  // (#template-pool/<containerId>) so it is deep-linkable and closes on
  // browser Back.
  const {
    open: poolOpen,
    param: poolContainerId,
    openWith: openPool,
    onOpenChange: closePool
  } = useHashParamDialog(OCR_HASH.templatePool);
  const poolDialogOpen = poolOpen && poolContainerId === String(containerId);

  async function handleLegacyStart() {
    try {
      await startMutation.mutateAsync({ containerId });
      toasts.started(containerId, containerLabel);
    } catch {
      // handled by global MutationCache
    }
  }

  const onClick = () => {
    if (registrationId && registrationId > 0) {
      openPool(String(containerId));
    } else {
      void handleLegacyStart();
    }
  };

  return (
    <PermissionGate permissions={['ocr.edit']}>
      <Button
        onClick={onClick}
        disabled={disabled || startMutation.isPending}
        variant='outline'
      >
        {startMutation.isPending ? (
          <Loader2 className='me-2 h-4 w-4 animate-spin' />
        ) : (
          <ScanText className='me-2 h-4 w-4' />
        )}
        {t('startOcr')}
      </Button>

      {registrationId ? (
        <OcrTemplatePoolDialog
          open={poolDialogOpen}
          onOpenChange={closePool}
          containerId={containerId}
          registrationId={registrationId}
          containerLabel={containerLabel}
        />
      ) : null}
    </PermissionGate>
  );
}
